import { useCallback } from 'react';
import type { NavigateFunction } from 'react-router';
import { apiService } from '@/services/ApiService';
import { directBookingStorage } from '@/components/direct-booking/directBookingStorage';
import { useDirectBookingStore } from '@/stores/direct-booking/useDirectBookingStore';
import type { SelfReferralFormData } from '@/components/self-referral/types';

interface DirectBookingActionDeps {
  dietitianId?: string;
  isAuthenticated: boolean;
  claimReferral: (token: string) => Promise<unknown>;
  navigate: NavigateFunction;
}

export const useDirectBookingActions = ({
  dietitianId,
  isAuthenticated,
  claimReferral,
  navigate,
}: DirectBookingActionDeps) => {
  const handleInfoSubmit = useCallback(async (formData: SelfReferralFormData) => {
    if (!dietitianId) return;

    const {
      setProcessing,
      setError,
      setDietitian,
      setSessionData,
      setStep,
    } = useDirectBookingStore.getState();

    setProcessing(true);
    setError(null);

    try {
      const providerId = import.meta.env.VITE_DEFAULT_PROVIDER_REFERRAL_ID;
      const response = await apiService.selfReferral.createDirectSession(dietitianId, providerId, formData);
      const matched = response.matched_dieticians?.[0];

      if (matched) {
        setDietitian(matched);
      }

      setSessionData(response);

      directBookingStorage.saveSession(
        {
          currentStep: 'confirmation',
          formData,
          sessionData: response,
          isLoading: false,
          selectedDietitian: response.matched_dieticians[0],
        },
        response.session_token,
        response.expires_at
      );

      setStep('confirmation');
    } catch {
      setError('Failed to process information. Please try again.');
    } finally {
      setProcessing(false);
    }
  }, [dietitianId]);

  const handleConfirmation = useCallback(async () => {
    const {
      sessionData,
      dietitian,
      setProcessing,
      setError,
    } = useDirectBookingStore.getState();

    if (!sessionData || !dietitian) return;

    setProcessing(true);

    try {
      const targetDietitian = sessionData.matched_dieticians[0];
      const finalizeResponse = await apiService.selfReferral.selectDietitian(
        sessionData.session_token,
        targetDietitian.dietician_id
      );

      directBookingStorage.updateWorkflow({
        currentStep: 'completed',
        selectedDietitian: targetDietitian,
        finalReferral: finalizeResponse
      });

      if (isAuthenticated) {
        try {
          await claimReferral(finalizeResponse.referral_token);
          navigate('/', { replace: true });
          navigate('/dashboard');
        } catch (claimError) {
          console.error('Failed to claim referral', claimError);
        }
      } else {
        navigate(`/patient/register?referral_token=${finalizeResponse.referral_token}`);
      }
    } catch {
      setError('Failed to finalize booking. Please try again.');
    } finally {
      setProcessing(false);
    }
  }, [claimReferral, isAuthenticated, navigate]);

  const loadUserContext = useCallback(async (userId: string) => {
    const {
      setLoading,
      setPatientProfile,
      setPrefilledConditions,
    } = useDirectBookingStore.getState();

    try {
      const profilePromise = apiService?.patient?.getProfile
        ? apiService.patient.getProfile()
        : null;
      const referralsPromise = apiService?.referral?.listReferrals
        ? apiService.referral.listReferrals({
            patientId: userId,
            limit: 1,
            sortBy: 'updatedAt',
            sortOrder: 'desc'
          })
        : null;

      const [profile, referrals] = await Promise.all([profilePromise, referralsPromise]);

      if (profile) {
        setPatientProfile(profile);
      }

      const referralWithConditions = referrals?.referrals?.find(
        (ref) => Array.isArray(ref.conditions) && ref.conditions.length > 0
      );

      if (referralWithConditions?.conditions) {
        setPrefilledConditions(referralWithConditions.conditions);
      }
    } catch (err) {
      console.warn('DirectBookingWizard: Failed to load user context', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    handleInfoSubmit,
    handleConfirmation,
    loadUserContext,
  };
};
